import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { HttpError } from '@core/errors/http-errors';
import { UnauthorizedHandlerService } from '@core/services/unauthorized-handler.service';
import { isApiRequest } from './api-request.guard';

const CONNECTION_ERROR =
  'Unable to connect. Please check your internet connection and try again.';
const UNAVAILABLE_ERROR =
  'Server is temporarily unavailable. Please try again in a few moments.';

/**
 * Maps raw `HttpErrorResponse`s into the domain `HttpError` shape that ApiService
 * callers expect, centralizing the messages that were previously duplicated across
 * every verb. On 401 it drives the shared logout flow (then throws
 * `UnauthorizedError`).
 */
export const errorMappingInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const unauthorizedHandler = inject(UnauthorizedHandlerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        try {
          // Triggers logout (once) and throws UnauthorizedError.
          unauthorizedHandler.handle();
        } catch (unauthorizedError) {
          return throwError(() => unauthorizedError);
        }
      }
      return throwError(() => mapToHttpError(error));
    }),
  );
};

function mapToHttpError(error: HttpErrorResponse): HttpError {
  if (error.status === 0) {
    return new HttpError(0, CONNECTION_ERROR);
  }
  if (error.status === 503) {
    return new HttpError(503, UNAVAILABLE_ERROR);
  }
  return new HttpError(error.status, extractMessage(error));
}

function extractMessage(error: HttpErrorResponse): string {
  const body = error.error as unknown;
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    const message = record['message'] ?? record['error'];
    if (typeof message === 'string') {
      return message;
    }
  }
  return `HTTP ${error.status}`;
}
